import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Contract limit shared by every server-paginated search: below it, no query. */
export const MIN_SEARCH_LENGTH = 2;

/** Long enough to swallow typing, short enough not to feel like a lag. */
const SEARCH_DEBOUNCE_MS = 300;

const readPage = (value: string | null) => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : 1;
};

export interface UrlListParams {
  page: number;
  /** What the server is being asked for: empty until the input is long enough. */
  search: string;
  /** What the operator has typed, which the URL only catches up with later. */
  searchInput: string;
  searchParams: URLSearchParams;
  setPage: (page: number) => void;
  setSearchInput: (value: string) => void;
  /**
   * Edits the query string and sends the list back to the first page, which
   * every filter change has to do: page 3 of the previous filter describes
   * nothing.
   */
  updateFilter: (update: (params: URLSearchParams) => void) => void;
  /**
   * Reads a comma-separated multi-value filter. Values the list does not admit
   * are dropped rather than sent: a hand-written query string must not turn
   * into a request the server can only refuse.
   */
  readSet: (name: string, isAllowed?: (value: string) => boolean) => string[];
  /** Adds or removes one value of such a filter, back to the first page. */
  toggleInSet: (
    name: string,
    value: string,
    isAllowed?: (value: string) => boolean
  ) => void;
}

/**
 * The part of a server-paginated list that lives in the URL: the page and the
 * debounced search. A list adds its own filters through `updateFilter`, so
 * every list reloads, shares and goes back to the same view the same way.
 */
const useUrlListParams = (): UrlListParams => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = readPage(searchParams.get('page'));
  const search = searchParams.get('search')?.trim() ?? '';
  const [searchInput, setSearchInput] = useState(search);
  const pendingSearchInput = useRef<string | null>(null);

  useEffect(() => {
    if (pendingSearchInput.current !== null) {
      const pendingSearch = pendingSearchInput.current;
      const expectedSearch =
        pendingSearch.length >= MIN_SEARCH_LENGTH ? pendingSearch : '';
      if (search === expectedSearch) {
        pendingSearchInput.current = null;
        return;
      }
    }
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    const nextSearch = searchInput.trim();
    if (nextSearch === search || (nextSearch.length === 1 && !search)) return;

    const timeout = window.setTimeout(() => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('page');
      if (nextSearch.length >= MIN_SEARCH_LENGTH)
        nextParams.set('search', nextSearch);
      else nextParams.delete('search');
      pendingSearchInput.current = nextSearch;
      setSearchParams(nextParams);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [search, searchInput, searchParams, setSearchParams]);

  const updateFilter = (update: (params: URLSearchParams) => void) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('page');
    update(nextParams);
    setSearchParams(nextParams);
  };

  const readSet = (
    name: string,
    isAllowed: (value: string) => boolean = () => true
  ) =>
    (searchParams.get(name) ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter((value) => Boolean(value) && isAllowed(value))
      .filter((value, index, values) => values.indexOf(value) === index);

  const toggleInSet = (
    name: string,
    value: string,
    isAllowed?: (value: string) => boolean
  ) => {
    const current = readSet(name, isAllowed);
    const next = current.includes(value)
      ? current.filter((candidate) => candidate !== value)
      : [...current, value];
    updateFilter((nextParams) => {
      if (next.length) nextParams.set(name, next.join(','));
      else nextParams.delete(name);
    });
  };

  const setPage = (nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextPage === 1) nextParams.delete('page');
    else nextParams.set('page', String(nextPage));
    setSearchParams(nextParams);
  };

  return {
    page,
    search,
    searchInput,
    searchParams,
    setPage,
    setSearchInput,
    updateFilter,
    readSet,
    toggleInSet,
  };
};

export default useUrlListParams;
